import { useContext, useEffect, useState } from 'react';
import { Accordion, Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { StepWizardChildProps } from 'react-step-wizard';
import { MaterialsBranchTable } from '../components/Dashboard/MaterialBranchTable2';
import { BranchesContext } from '../context/BranchesContext';
import { RequestContext } from '../context/RequestContext';
import { SkeletonItem, TaskBranchInfo } from '../models/dashboard-models';
import { useAsyncResource } from 'use-async-resource';
import { useGetAllTaskSkeletons } from '../utils/work-hooks';
import {AuthContext} from "../context/AuthContext";
import {Role} from "../utils/Enums/Roles";
import { useGetUserAvailableItemsIds } from "../utils/warehouses-hooks";
import {ShortDevicePassport} from "../models/warehouse-models";

interface Material {
    name: string;
    count: number;
}
export const MaterialsSelect: React.FC<Partial<StepWizardChildProps>> = ({ nextStep, previousStep }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    const { hasPermission, loggedUser } = useContext(AuthContext);
    const isDistributor = !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR])||hasPermission([Role.INSTALLER]));
    const [ getAllAvailableItems] = useAsyncResource(useGetUserAvailableItemsIds, loggedUser?.id);
    const availableItems = getAllAvailableItems();

    const { taskDescription, tasksBranchesInfo, setTasksBranchesInfo } = useContext(RequestContext);
    const { materialsPassports, devicePassports, selectedBranches } = useContext(BranchesContext);
    const selectedBranchesIds = selectedBranches.map((branch) => branch.id.toString());
    const [totalMaterials, setTotalMaterials] = useState<Material[]>([] as Material[]);
    const allPassports = materialsPassports.concat(devicePassports);
    const [availablePassports, setAvailablePassports] = useState<ShortDevicePassport[]>([] as ShortDevicePassport[]);

    const [readSkeletons] = useAsyncResource(useGetAllTaskSkeletons, selectedBranchesIds, taskDescription.category);
    const skeletons = readSkeletons();
    const getNewTotalMaterials = () => {
        let newItems = [] as Material[];
        tasksBranchesInfo.map((taskBranch) => {
            taskBranch.items.map((item) => {
                let material = newItems.findIndex((material) => material.name === item.passportId);
                if (material >= 0) {
                    newItems[material].count = newItems[material].count + item.count;
                } else {
                    newItems = [...newItems, { name: item.passportId, count: item.count }];
                }
            });
        });
        return newItems;
    };

    const onMaterialChange = (branchToUpdate: number, material: string | number, count?: number) => {
        let taskToUpdate = tasksBranchesInfo.find((task) => task.branchId === branchToUpdate);
        let itemToUpdate = taskToUpdate?.items.find((item) => item.passportId === material);

        let updatedItems: SkeletonItem[];
        let newTask: TaskBranchInfo;
        let updatedTasks: TaskBranchInfo[];

        if (taskToUpdate && itemToUpdate && count) {
            const newItem = { ...itemToUpdate, count };
            updatedItems = taskToUpdate.items.map((item) => (item.passportId !== material ? item : newItem));
            newTask = { ...taskToUpdate, items: updatedItems };
        } else if (taskToUpdate) {
            updatedItems = taskToUpdate.items.filter((item) => item.passportId !== material);
            newTask = { ...taskToUpdate, items: updatedItems };
        }

        updatedTasks = tasksBranchesInfo.map((task) => (task.branchId !== branchToUpdate ? task : newTask));
        setTasksBranchesInfo(updatedTasks);
    };

    const onMaterialAdd = (branchToUpdate: number, passportId: number, count: number) => {
        let taskToUpdate = tasksBranchesInfo.find((task) => task.branchId === branchToUpdate);
        const passportName = isDistributor ? availablePassports.find((el) => el.id === passportId)?.name : allPassports.find((el) => el.id === passportId)?.name;
        let newTask: TaskBranchInfo;

        if (taskToUpdate?.items.some((e) => e.passportId === passportName)) {
            const newItems = taskToUpdate.items.map((el) => {
                return el.passportId === passportName ? { ...el, count: el.count + count } : el;
            });
            newTask = { ...taskToUpdate, items: newItems };
        } else if (passportName && taskToUpdate) {
            const newItems = [...taskToUpdate.items, { itemId: passportId, passportId: passportName, count }];
            newTask = { ...taskToUpdate, items: newItems };
        }
        const updatedTasks = tasksBranchesInfo.map((task) => (task.branchId !== branchToUpdate ? task : newTask));

        setTasksBranchesInfo(updatedTasks);
    };

    useEffect(() => {
        const tasksBranches: TaskBranchInfo[] = selectedBranches.map((branch) => {
            let skeleton = skeletons?.find((skeleton) => skeleton.branchId === branch.id);
            return {
                branchId: branch.id,
                items: skeleton?.items || [],
                handover: false,
                branchName: branch.name ?? '',
                reason: '',
                rentalAgreement: false,
                details: '',
                targetDate: taskDescription.targetDate,
                city: branch.city ?? '',
                companyName: branch.companyName ?? '',
                branchAddress: branch.branchAddress ?? '',
            };
        });
        setTasksBranchesInfo(tasksBranches);
    }, [skeletons]);

    useEffect(() => {
        const newTotalMaterials = getNewTotalMaterials();
        setTotalMaterials(newTotalMaterials);
    }, [tasksBranchesInfo]);

    useEffect( () => {
        if(isDistributor) {
            availableItems.map((number)=>
                setAvailablePassports((prevPassports) => [
                    ...prevPassports,
                    ...allPassports.filter((item) => item.id === number)
                ])
            )
        }
    }, []);

    return (
        <>
            <div className='wizard-content d-flex flex-wrap justify-content-between w-100'>
                <div className='d-flex flex-column accordion-container w-100 px-2'>
                    <h5 className='pb-2'>
                        {t('materialsOnBranches')} - {t('distributor')}: {taskDescription.assigneeName}
                    </h5>
                    <Accordion defaultActiveKey='0' className='my-2 accordion'>
                        {selectedBranches.map((branch, index) => (
                            <Accordion.Item key={index} id={`${branch.id}`} eventKey={`${index}`}>
                                <Accordion.Header>{branch.branchAddress}</Accordion.Header>
                                <Accordion.Body className='pb-3 px-1 pt-1'>
                                    <MaterialsBranchTable
                                        items={
                                            tasksBranchesInfo.find((taskBranch) => taskBranch.branchId === branch.id)
                                                ?.items || ([] as SkeletonItem[])
                                        }
                                        branchId={branch.id}
                                        passports={isDistributor?availablePassports:allPassports}
                                        {...{ onMaterialChange, onMaterialAdd }}
                                    ></MaterialsBranchTable>
                                </Accordion.Body>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
                <div className='w-100 px-2 total-materials'>
                    <h5 className='pb-2'>{t('totalMaterials')}</h5>
                    <Table striped hover responsive className='mt-2 border border-dark'>
                        <thead>
                            <tr>
                                <th className='fw-bold w-25 text-center'>{t('materials')}</th>
                                <th className='fw-bold w-25  text-center'>{t('quantity')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {totalMaterials.map((item, index) => (
                                <tr key={index}>
                                    <td className='text-center'>{item.name}</td>
                                    <td className='text-center'>{item.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
            <div className='w-100 d-flex wizard-buttons justify-content-between'>
                <Button onClick={() => previousStep!()}>{t('previous')}</Button>
                <Button className='ms-3' onClick={() => nextStep!()}>
                    {t('next')}
                </Button>
            </div>
        </>
    );
};
